import { PackInstance } from "@alienheadwars/football-card-manager-model";
import { Box, Card } from "@mui/material";
// Added useCallback
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
// Removed direct motion import, using AnimatedDiv instead
import { useHistory, useParams } from "react-router-dom";
import { getCallableFunction } from "../../../../firebase/firebase";
import { listenToDocument } from "../../../../firebase/firestore";
import { localise } from "../../../../locale/locale";
import { AnimatedDiv } from "../../../MotionMui/MotionMui"; // Added import for AnimatedDiv
import { ButtonComponent } from "../../../base/button/Button";
import { Loading } from "../../../base/loading/Loading";
import { PageContainerComponent } from "../../../base/pageContainer/PageContainer";
import { HelpOverlay } from "../../../base/tutorial/helpOverlay/HelpOverlay";
import { PlayerCardComponent } from "../../../cards/playerCards/PlayerCard";
import { TeamContext } from "../../TeamContext";
import { PackSummary } from "./PackSummary";

export interface PackUrlParams {
    teamId: string;
    packId: string;
}

const ANIMATION_DURATION = 500; // Keep existing constant

export const ViewPackInstance = () => {
    const teamContext = useContext(TeamContext);
    const history = useHistory();
    const packUrlParams = useParams<PackUrlParams>();

    const [pack, setPack] = useState<PackInstance | undefined>(undefined);
    const [openPackPromise, setOpenPackPromise] = useState<Promise<any> | undefined>(undefined);
    // currentIndex now tracks the index of the card *at the top* / *to be revealed next*
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    // Set of indices currently animating away
    const [animatingIndices, setAnimatingIndices] = useState<Set<number>>(new Set());
    const [showSummary, setShowSummary] = useState<boolean>(false);
    // Ref to store timeout IDs, keyed by card index
    const timeoutRefs = useRef<Map<number, number>>(new Map());

    // Calculate total items once pack is loaded (cover + assignedItems)
    const totalItemsInStack = useMemo(() => pack?.assignedItems ? pack.assignedItems.length + 1 : 0, [pack]);

    // --- Effect for fetching/listening to pack data (unchanged) ---
    useEffect(() => {
        let isMounted = true;
        let unsubscribe: (() => void) | undefined;
        const currentTimeoutMap = timeoutRefs.current;
        const fetchAndListen = async () => {
            const existingPack = teamContext.getUnopenedPacks()?.find(p => p.uid === packUrlParams.packId);
            if (isMounted && existingPack) {
                setPack(existingPack);
            }
            try {
                unsubscribe = listenToDocument(
                    `teams/${packUrlParams.teamId}/packs/${packUrlParams.packId}`,
                    (data) => {
                        if (isMounted) {
                            setPack(data);
                        }
                    }
                );
            } catch (error) {
                console.error("Error setting up pack listener:", error)
            }
        };
        fetchAndListen();
        return () => {
            isMounted = false;
            if (unsubscribe) unsubscribe();
            console.log("Cleaning up timeouts:", currentTimeoutMap);
            currentTimeoutMap.forEach(timeoutId => clearTimeout(timeoutId));
        };
    }, [teamContext, packUrlParams.teamId, packUrlParams.packId]);

    // --- Effect for calling the open pack function (unchanged) ---
    useEffect(() => {
        if (pack && !pack.opened && !openPackPromise) {
            const openPackFunction = getCallableFunction('v2OpenPack');
            setOpenPackPromise(openPackFunction({ packId: pack.uid }));
        }
    }, [pack, openPackPromise]);


    // --- Define items array, memoized based on pack (unchanged) ---
    const items = useMemo(() => {
        if (!pack?.assignedItems) return [];
        const coverKey = "pack-cover"; // Use a specific key for the cover
        return [
            // Cover Item (ensure it has a unique key if PlayerCardComponent isn't used)
            <Card key={coverKey} sx={{ borderRadius: '2%' }}>
                <img
                    // Removed redundant key here, key is on Card
                    src={`/images/${pack.art[0]?.image}.png`}
                    alt={pack.id}
                    style={{
                        width: 'min(95vw, 65vh)',
                        display: 'block'
                    }}
                />
            </Card>,
            // Player Card Items
            ...(pack.assignedItems || []).map((player) => {
                // Use player.id as key assuming it's unique and stable
                return <PlayerCardComponent key={player.id} player={player} />;
            }).reverse() // Keep reverse to match original stacking logic
        ];
    }, [pack]);


    // --- Handle Card Tap (unchanged) ---
    const handleCardTap = useCallback(() => {
        if (showSummary || totalItemsInStack === 0) return;
        const indexToAnimate = currentIndex;
        if (indexToAnimate >= totalItemsInStack) return;

        setAnimatingIndices(prev => new Set(prev).add(indexToAnimate));

        const nextIndex = currentIndex + 1;
        const isLastCardTap = nextIndex >= totalItemsInStack;

        if (isLastCardTap) {
            setShowSummary(true);
        }

        setCurrentIndex(nextIndex);

        const existingTimeoutId = timeoutRefs.current.get(indexToAnimate);
        if (existingTimeoutId) {
            clearTimeout(existingTimeoutId);
        }

        const newTimeoutId = window.setTimeout(() => { // Use window.setTimeout for clarity if needed
            setAnimatingIndices(prev => {
                const nextSet = new Set(prev);
                nextSet.delete(indexToAnimate);
                return nextSet;
            });
            timeoutRefs.current.delete(indexToAnimate);
        }, ANIMATION_DURATION);

        timeoutRefs.current.set(indexToAnimate, newTimeoutId);

    }, [currentIndex, showSummary, totalItemsInStack]);


    // --- Loading State (unchanged) ---
    if (!pack || !pack.assignedItems || totalItemsInStack === 0) {
        return <Loading />;
    }

    // --- Confirm Action (unchanged) ---
    const confirmAction = async () => {
        if (openPackPromise) {
            try {
                await openPackPromise;
            } catch (error) {
                console.error("Error resolving openPackPromise:", error);
            }
        }
        history.goBack();
    };

    // --- Render Logic ---
    return (
        <PageContainerComponent key={pack.uid}>
            {showSummary ? (
                // --- Summary View (unchanged) ---
                <>
                    <PackSummary pack={pack} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
                        <ButtonComponent onClick={confirmAction} loadOnExecute>
                            {localise('pack.confirmButton')}
                        </ButtonComponent>
                    </Box>
                </>
            ) : (
                // --- Card Stacking View ---
                <>
                    <HelpOverlay
                        helpText={localise('openPack.helpText')}
                        tutorialId="openPack.helpText"
                        blockSkip={true}
                        additionalCondition={currentIndex === 0}
                        textHeight="10vh"
                    >
                        {/* Container Box receives the tap (unchanged) */}
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '90vh',
                                width: '100vw'
                            }}
                            onClick={handleCardTap} // Tap triggers animation
                        >

                            {/* --- Render Absolutely Positioned Cards using AnimatedDiv --- */}
                            {items.map((item, index) => {
                                const isAnimatingAway = animatingIndices.has(index);
                                const isTopCard = index === currentIndex && !isAnimatingAway;
                                const isGone = index < currentIndex && !isAnimatingAway;
                                // Define animation variants
                                const cardAnimateProps = {
                                    hidden: { opacity: 0, y: 0, display: 'flex' },
                                    visible: { opacity: 1, y: 0, display: 'flex' },
                                    swipingAway: { opacity: 1, y: '100vh', display: 'flex' }, // Using opacity: 1 during swipe
                                    gone: { display: 'none' }
                                };

                                // Determine the target animation state
                                let targetState: keyof typeof cardAnimateProps;
                                if (isGone) {
                                    targetState = 'gone';
                                } else if (isAnimatingAway) {
                                    targetState = 'swipingAway';
                                } else if (isTopCard) {
                                    targetState = 'visible';
                                } else {
                                    targetState = 'hidden';
                                }
                                if (targetState === 'hidden') {
                                    return null
                                }

                                console.log(index)
                                console.log(item.key)
                                console.log(targetState)

                                return (
                                    // Use AnimatedDiv
                                    <AnimatedDiv
                                        key={item.key} // Use the key from the items array
                                        initial={index === 0 ? "visible" : "hidden"} // Start cover visible, others hidden
                                        animate={targetState}
                                        variants={cardAnimateProps}
                                        transition={{
                                            opacity: { duration: 0.2, ease: "linear" }, // Quick fade-in
                                            y: { duration: ANIMATION_DURATION / 1000, ease: "easeIn" } // Swipe away uses easeIn
                                        }}
                                        style={{
                                            // Basic styles for positioning and stacking
                                            justifyContent: 'center',
                                            position: 'absolute',
                                            top: 0, left: 0, right: 0, margin: 'auto', // Centering
                                            width: 'min(95vw, 65vh)', // Sizing
                                            pointerEvents: 'none',
                                            // zIndex: items.length - index, // Stacking order
                                            // transform and transition removed, handled by motion
                                            // display handled by variants
                                        }}
                                    >
                                        {item} {/* Render the actual Card or Img */}
                                    </AnimatedDiv>
                                );
                            })}
                        </Box>
                    </HelpOverlay>
                </>
            )}
        </PageContainerComponent>
    );
};